import type { TurnstileWidgetHandle } from '@instanexus/ui';
import { useCallback, useRef, useState, type RefObject } from 'react';

/** Public site key, injected at build time (`vite.config.ts`). Blank = not configured. */
const SITE_KEY: string = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';

export interface UseCaptcha {
  /** `''` when the build has no key — the form must then refuse to submit and say so. */
  readonly siteKey: string;
  readonly isConfigured: boolean;
  /** The current single-use token, or `null` until the challenge passes / after expiry. */
  readonly token: string | null;
  readonly setToken: (token: string | null) => void;
  readonly widgetRef: RefObject<TurnstileWidgetHandle | null>;
  /** Re-arm after EVERY submit (success or failure): a Turnstile token is single-use. */
  readonly reset: () => void;
}

/** State shared by every form that carries a Turnstile challenge; pairs with `CaptchaField`. */
export function useCaptcha(): UseCaptcha {
  const [token, setToken] = useState<string | null>(null);
  const widgetRef = useRef<TurnstileWidgetHandle | null>(null);
  const reset = useCallback((): void => {
    setToken(null);
    widgetRef.current?.reset();
  }, []);
  return {
    siteKey: SITE_KEY,
    isConfigured: SITE_KEY.length > 0,
    token,
    setToken,
    widgetRef,
    reset,
  };
}
